import type { FirebaseMessagingTypes } from '@react-native-firebase/messaging';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import { Event, track } from './analytics';
import type { CustomerIOMessageType } from 'app/types/customerIO';
import { pushNotifRegistrationInstance } from 'app/lib/push-notifications';
import { http } from 'app/lib/axios';
import type { MessagesPushWatcher } from 'app/network/chat/message';

import type { DMData } from 'app/types/notification';
import { changeAppIcon } from 'app/utils/helpers';
import { updateBadgeData } from 'app/lib/badge';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

export async function hasNotificationPermission() {
  const settings = await Notifications.getPermissionsAsync();
  return settings.granted || settings.ios?.status === Notifications.IosAuthorizationStatus.PROVISIONAL;
}

export async function requestNotificationPermission() {
  const status = await Notifications.requestPermissionsAsync();
  track(Event.NotificationPermissionStatus, {
    status: status.granted ? 'granted' : 'denied',
  });
  if (status.granted) {
    void pushNotifRegistrationInstance(http).registerForRemoteNotifications();
  }
  return status.granted;
}

export async function onRemoteMessage(
  message: FirebaseMessagingTypes.RemoteMessage,
  messagesPushWatcher?: MessagesPushWatcher,
) {
  changeAppIcon();

  const handled = await onRemoteChatMessage(message, messagesPushWatcher);
  if (handled) return;

  if (!message.data) return;

  const messageData: CustomerIOMessageType = message.data as unknown as CustomerIOMessageType;

  void updateBadgeData({}, 'onRemoteMessage');

  await Notifications.scheduleNotificationAsync({
    content: {
      title: messageData.title,
      body: messageData.body,
      data: messageData,
    },
    trigger: null,
  });
}

async function onRemoteChatMessage(
  message: FirebaseMessagingTypes.RemoteMessage,
  messagesPushWatcher?: MessagesPushWatcher,
) {
  if (!message.data) return false;

  if (!message.data.unread_message_count) return false;

  const payload = message.data as unknown as DMData;
  console.log('[onRemoteChatMessage] Received notification', payload);

  if (messagesPushWatcher?.pushNotifHandled(payload.channel_url)) {
    // Above function returns true if it does NOT want a push notif to be shown.
    // ex. when the chat screen it in foreground, and it will be shown there.
    console.log(
      `[onRemoteChatMessage] messagesPushWatcher.pushNotifHandled(${payload.channel_url}) === true; won't show push.`,
    );
    return true;
  }

  void updateBadgeData(payload, 'onRemoteChatMessage');

  if (payload.title) {
    await setupPushChannels();

    console.log('[onRemoteChatMessage] Notifications.scheduleNotificationAsync');
    await Notifications.scheduleNotificationAsync({
      content: {
        title: payload.title,
        body: payload.body || payload.message,
        data: payload,
      },
      trigger: {
        channelId: 'chat',
        seconds: 1,
      },
    });
  }

  return true;
}

async function setupPushChannels() {
  if (Platform.OS === 'android') {
    await Notifications.deleteNotificationChannelAsync('sendbird-chat');
    await Notifications.setNotificationChannelAsync('chat', {
      name: 'Messages',
      importance: Notifications.AndroidImportance.MAX,
      enableVibrate: true,
      vibrationPattern: [0, 250, 250, 50, 250],
      enableLights: true,
      lightColor: theme.colors['surface.tertiary'],
      lockscreenVisibility: Notifications.AndroidNotificationVisibility.PUBLIC,
      showBadge: true,
      sound: 'default',
    });
  }
}

// On iOS, we will receive only title & body when clicking the push notif, so we need to save the link when receiving the push, to be used when we process the click later on
const receivedNotifications: Record<string, DMData> = {};

export function saveNotificationLink(payload: DMData) {
  const key = `${payload.title}${payload.body}`;
  receivedNotifications[key] = payload;
}

function getSavedNotification(title?: string | null, body?: string | null) {
  if (!title || !body) return undefined;
  const key = `${title}${body}`;
  const payload = receivedNotifications[key];
  if (payload) {
    delete receivedNotifications[key];
    return payload;
  }
}

export function getNotificationLink(title?: string | null, body?: string | null) {
  const payload = getSavedNotification(title, body);
  if (payload) {
    return payload.link;
  }
}
